/**
 * The sections of `cycle-step.html` for step 5 (f10.8, design D8): the drop
 * zone, "File dropped", "What the system did with it" and "Resulting rates".
 * Every fact and every rate is the server's, rendered verbatim.
 */

import { Fragment, useRef, useState, type ReactNode } from 'react';
import { AlertCircle, CheckCircle2, Coins, FileSpreadsheet, Repeat, UploadCloud } from 'lucide-react';

import type { ExchangeRatesReview } from '@/api/client';
import { formatBytes, formatWhen, shortChecksum, statePresentation } from '@/routes/data-import/importView';
import { EmptyCell } from '@/ui/EmptyCell';

import {
  COMPARISON_CURRENCY,
  deltaClass,
  severityPill,
  statusPill,
  transformations,
  usdHint,
} from './exchangeRatesView';

function useFilePicker(onFile: (file: File) => void) {
  const input = useRef<HTMLInputElement>(null);
  const element = (
    <input
      ref={input}
      type="file"
      className="sr-only"
      aria-label="Exchange-rate list file"
      onChange={(event) => {
        const file = event.target.files?.item(0);
        if (file) onFile(file);
        event.target.value = '';
      }}
    />
  );
  return { open: () => input.current?.click(), element };
}

export function ExchangeRatesDropzone({ onFile, busy }: { onFile: (file: File) => void; busy: boolean }) {
  const [over, setOver] = useState(false);
  const picker = useFilePicker(onFile);
  return (
    <div
      className={`dropzone${over ? ' is-over' : ''}`}
      onDragOver={(event) => {
        event.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setOver(false);
        const file = event.dataTransfer.files.item(0);
        if (file) onFile(file);
      }}
    >
      <Coins className="dz-icon" aria-hidden="true" />
      <div className="dz-title">Drop the corporate exchange-rate list</div>
      <div className="hint">
        Each rate is EUR per 1 unit of the currency. EUR must be exactly 1 and USD must be present; the rates
        become this cycle&rsquo;s own and earlier cycles keep theirs.
      </div>
      <div className="btn-row dz-actions">
        <button type="button" className="btn btn-primary" disabled={busy} onClick={picker.open}>
          <UploadCloud aria-hidden="true" /> Choose file
        </button>
      </div>
      <div className="hint">The format and the header row are detected from the file&rsquo;s content</div>
      {picker.element}
    </div>
  );
}

export function ExchangeRatesFileCard({
  review,
  onReplace,
  busy,
}: {
  review: ExchangeRatesReview;
  onReplace: (file: File) => void;
  busy: boolean;
}) {
  const { batch } = review;
  const picker = useFilePicker(onReplace);
  const state = statePresentation(batch.state.code);
  const staged = batch.state.code === 'STAGED' || batch.state.code === 'AWAITING_RESOLUTION';
  return (
    <div className="card">
      <div className="card-head">
        <h3>File dropped</h3>
        {batch.state.code === 'COMMITTED' ? (
          <span className="badge pill-success">{batch.is_current ? 'Committed — current rates' : 'Committed'}</span>
        ) : staged ? (
          <span className="badge pill-info">Staged — not committed</span>
        ) : (
          <span className={`badge ${state.pill}`}>{state.label}</span>
        )}
      </div>
      <div className="card-body">
        <div className="dropzone is-filled">
          <div className="row us-eng-file">
            <FileSpreadsheet className="dz-icon" aria-hidden="true" />
            <div className="us-eng-file-text">
              <div className="dz-title truncate">{batch.original_filename ?? 'The uploaded file'}</div>
              <div className="hint">
                {formatBytes(batch.byte_size)} &middot; dropped {formatWhen(batch.uploaded_at)}
                {batch.uploaded_by ? ` by ${batch.uploaded_by.name}` : ''} &middot; batch #{batch.batch_number}
              </div>
            </div>
            <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={picker.open}>
              <Repeat aria-hidden="true" /> Replace
            </button>
          </div>
          {picker.element}
        </div>
      </div>
    </div>
  );
}

export function ExchangeRatesProcessingCard({ review }: { review: ExchangeRatesReview }) {
  const { batch, issues } = review;
  return (
    <div className="card">
      <div className="card-head">
        <h3>What the system did with it</h3>
        <span className="hint">
          {issues.counts.ERROR ?? 0} error &middot; {issues.counts.WARNING ?? 0} warning &middot;{' '}
          {issues.counts.INFO ?? 0} info
        </span>
      </div>
      <div className="card-body stack">
        <dl className="dl cols-4">
          <div>
            <dt>Detected format</dt>
            <dd>
              {batch.detected_format ?? <EmptyCell />} <span className="hint">sniffed by content</span>
            </dd>
          </div>
          <div>
            <dt>Header row</dt>
            <dd>{batch.detected_header_row ?? <EmptyCell />}</dd>
          </div>
          <div>
            <dt>Rows parsed / kept / dropped</dt>
            <dd>
              {batch.rows_in ?? '—'} / {batch.rows_out ?? '—'} / {batch.rows_dropped ?? '—'}
            </dd>
          </div>
          <div>
            <dt>Checksum</dt>
            <dd className="mono">{shortChecksum(batch.content_sha256)}</dd>
          </div>
        </dl>

        <div>
          <div className="stat-label">Transformations applied</div>
          <ul className="stack-sm us-eng-list">
            {transformations(review).map((item) => (
              <li key={item.text} className="row us-eng-transform">
                {item.ok ? (
                  <CheckCircle2 className="us-eng-ok" aria-hidden="true" />
                ) : (
                  <AlertCircle className="us-eng-warn" aria-hidden="true" />
                )}
                <span>{item.text}</span>
              </li>
            ))}
          </ul>
        </div>

        {issues.items.length > 0 && (
          <div>
            <div className="stat-label">Issues</div>
            <ul className="stack-sm us-eng-list">
              {issues.items.map((issue, index) => (
                <li key={`${issue.code}-${index}`} className="row us-eng-issue">
                  <span className={severityPill(issue.severity)}>{issue.severity}</span>
                  <span className="mono">{issue.code}</span>
                  <span className="us-eng-issue-text">
                    {issue.message}
                    {issue.source_row_number !== null && (
                      <span className="hint"> &middot; row {issue.source_row_number}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, foot }: { label: string; value: ReactNode; foot: ReactNode }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      <div className="stat-foot">{foot}</div>
    </div>
  );
}

const COLUMNS = 7;

export function ExchangeRatesTableCard({ review, footer }: { review: ExchangeRatesReview; footer?: ReactNode }) {
  const { figures, rows } = review;
  return (
    <div className="card">
      <div className="card-head">
        <h3>Resulting rates</h3>
        <span className="hint">EUR per 1 unit of each currency &middot; this cycle only</span>
      </div>
      <div className="card-body stack">
        <div className="stat-grid">
          <Stat
            label="Rates to write"
            value={figures.rates_to_write}
            foot={`${figures.currencies_not_in_lookup} not stored · ${figures.rows_invalid} invalid`}
          />
          <Stat
            label="Rates superseded"
            value={figures.rates_to_supersede}
            foot={figures.rates_to_supersede ? "This cycle's current set, kept as history" : 'Nothing committed yet'}
          />
          <Stat label="USD rate" value={<span className="mono">{figures.usd_rate ?? '—'}</span>} foot="EUR per USD" />
          <Stat
            label="1 EUR in USD"
            value={<span className="mono">{figures.eur_per_usd_inverse ?? '—'}</span>}
            foot="Display only"
          />
        </div>

        <div className="table-scroll">
          <table className="data compact fx-rates">
            <thead>
              <tr>
                <th>Currency</th>
                <th>Name</th>
                <th className="num">EUR per 1 unit</th>
                <th className="num">Current rate</th>
                <th className="num">Delta</th>
                <th>Status</th>
                <th>Row</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isUsd = row.currency_code === COMPARISON_CURRENCY && row.status.code === 'VALID';
                const skipped = row.status.code !== 'VALID';
                return (
                  <Fragment key={row.source_row_number}>
                    <tr className={isUsd ? 'fx-row-usd' : skipped ? 'fx-row-skipped' : undefined}>
                      <td className="mono">{row.currency_code ?? row.currency_raw}</td>
                      <td>{row.currency_name ?? <EmptyCell />}</td>
                      <td className="num">{row.new_rate ?? row.rate_raw}</td>
                      <td className="num">{row.current_rate ?? <EmptyCell />}</td>
                      <td className={`num ${deltaClass(row.delta)}`}>{row.delta ?? <EmptyCell />}</td>
                      <td>
                        <span className={statusPill(row.status.code)} title={row.message ?? undefined}>
                          {row.status.name}
                        </span>
                      </td>
                      <td className="hint">{row.source_row_number}</td>
                    </tr>
                    {isUsd && figures.usd_rate && figures.eur_per_usd_inverse && (
                      <tr className="fx-row-usd-hint">
                        <td colSpan={COLUMNS}>{usdHint(figures.usd_rate, figures.eur_per_usd_inverse)}</td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {footer}
    </div>
  );
}
